import React, { useState } from 'react';
import { X } from 'lucide-react';
import { businessMappingService } from '../../../services/businessApi';

const initialFields = { plantId: '', startDate: '', firstAsnDate: '', newCode: '', pocName: '', pocPhone: '', pocEmail: '', notes: '' };

// Maps an already-known supplier onto another plant (its own onboarding checklist gets
// auto-created), or edits the plant-specific fields of an existing mapping. Pass `mapping`
// to edit — the plant becomes fixed and only the plant-specific fields are editable.
export default function AddToPlantModal({ supplierId, supplierName, availablePlants, mapping, onClose, onSaved }) {
  const isEdit = !!mapping;
  const [fields, setFields] = useState(isEdit ? {
    plantId: mapping.plant._id,
    startDate: mapping.startDate ? mapping.startDate.slice(0, 10) : '',
    firstAsnDate: mapping.firstAsnDate ? mapping.firstAsnDate.slice(0, 10) : '',
    newCode: mapping.newCode || '',
    pocName: mapping.poc?.name || '',
    pocPhone: mapping.poc?.contactPhone || '',
    pocEmail: mapping.poc?.contactEmail || '',
    notes: mapping.notes || '',
  } : initialFields);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const update = (field) => (e) => setFields(f => ({ ...f, [field]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!isEdit && !fields.plantId) { setError('Select a plant'); return; }

    setSaving(true);
    try {
      const payload = {
        startDate: fields.startDate || undefined,
        firstAsnDate: fields.firstAsnDate || undefined,
        newCode: fields.newCode || undefined,
        poc: { name: fields.pocName, contactPhone: fields.pocPhone, contactEmail: fields.pocEmail },
        notes: fields.notes || undefined,
      };
      if (isEdit) {
        await businessMappingService.update(mapping._id, payload);
      } else {
        await businessMappingService.create({ plantId: fields.plantId, supplierId, ...payload });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'add'} plant mapping`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="client-form-modal" style={{ margin: 'auto', width: 460, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? `Edit ${mapping.plant.name} mapping` : `Add ${supplierName} to a Plant`}</h3>
          <button className="icon-btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <form className="client-form" onSubmit={handleSave}>
          {error && <div className="form-error">{error}</div>}

          {!isEdit && (
            <div className="form-group">
              <label>Plant *</label>
              <select value={fields.plantId} onChange={update('plantId')} required>
                <option value="">Select plant...</option>
                {availablePlants.map(p => <option key={p._id} value={p._id}>{p.name} ({p.code})</option>)}
              </select>
              {availablePlants.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Already mapped to every active plant.
                </p>
              )}
            </div>
          )}

          <div className="form-section-title">Plant-specific details (optional)</div>
          <div className="form-row">
            <div className="form-group">
              <label>New Code (if different for this plant)</label>
              <input value={fields.newCode} onChange={update('newCode')} />
            </div>
            <div className="form-group">
              <label>Start date</label>
              <input type="date" value={fields.startDate} onChange={update('startDate')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>First ASN date</label>
              <input type="date" value={fields.firstAsnDate} onChange={update('firstAsnDate')} />
            </div>
            <div className="form-group">
              <label>POC name</label>
              <input value={fields.pocName} onChange={update('pocName')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>POC phone</label>
              <input value={fields.pocPhone} onChange={update('pocPhone')} />
            </div>
            <div className="form-group">
              <label>POC email</label>
              <input type="email" value={fields.pocEmail} onChange={update('pocEmail')} />
            </div>
          </div>
          <div className="form-group">
            <label>Comments</label>
            <textarea rows={2} value={fields.notes} onChange={update('notes')} />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || (!isEdit && availablePlants.length === 0)}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add to Plant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
